import { useCallback, useEffect, useRef, useState } from 'react';

import './PrivacyConsent.css';

const SCROLLBAR_THUMB_WIDTH = 79.8;
const SCROLLBAR_GAP = 10;

const formatLastUpdated = (date) => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date instanceof Date ? date : new Date(date));
};

const PERSONAL_DATA_ROWS = [
  {
    personalData: 'Name',
    purpose:
      'To personalize health education and \n provide product & services healthcare \n support.',
    legalBasis: 'Consent',
  },
  {
    personalData: 'Gender',
    purpose:
      'To personalize health education and \n provide product & services healthcare \n support.',
    legalBasis: 'Consent',
  },
  {
    personalData: 'Phone Number and Email',
    purpose:
      'To deliver appointment reminders, \n  conduct treatment adherence checks, \n  share educational and awareness \n  materials, provide app updates, and \n  offer paid optional specialized \n  healthcare modules.',
    legalBasis: 'Consent',
  },
  {
    personalData: 'Height and Weight',
    purpose:
      'To conduct Peak Flow Rate, gather Body \n  Mass Index (BMI) reading and other \n  health co-relation based on weight \n  fluctuations for providing suitable health \n  recommendations.',
    legalBasis: 'Consent',
  },
  {
    personalData: 'Age and Date of Birth',
    purpose:
      "To verify and determine the patient's \n  age for delivering age appropriate \n  health care.",
    legalBasis: 'Consent',
  },
  {
    personalData: {
      title:'',
          items: [
          'Health Data (Processed)',
        'Comorbid conditions',
        'Medical device/inhaler technique (correct/incorrect)',
        "Symptoms and it's severity",
        'Health vitals such as Heart Rate, Respiratory Rate, Blood Pressure, Blood Sugar, Body \n Temperature, Steps count, Sleep, Water intake, SPO2, Peak Flow Rate',
      ],
    },
    purpose:
      'To support in managing respiratory conditions (including Allergic Rhinitis); and to provide \n  tailored action plan and digital health education, while monitoring medication adherence, \n  managing clinical schedules, and recommending   appropriate paid specialized healthcare \n modules  and follow up visits via digital platforms \n  to enhance patient understanding, \n engagement,  and condition management',
    legalBasis: 'Consent',
  },
  {
    personalData: {
      title: '',
          items: [
          'Treatment Information (processed)',
        'Prescribed medication/ product details',
        'Medication adherence data',
        'Manufacturer/company name',
        'Quantity prescribed',
        'Treatment duration',
        'Pricing/cost information',
      ],
    },
    purpose:
      'To enable personalized counselling  \n  visits by reviewing prescribed medications (including \n molecule and dosage), and to deliver tailored health \n education and recommend appropriate paid \n specialized healthcare modules via digital platforms.',
    legalBasis: 'Consent',
  },
];

const PersonalDataCell = ({ data }) => {
  if (typeof data === 'string') {
    return <span>{data}</span>;
  }

  return (
    <div className="privacy-consent-personal-data-cell">
      <span className="privacy-consent-personal-data-cell-title">{data.title}</span>
      <div className="privacy-consent-personal-data-items">
        {data.items.map((item) => (
          <p key={item} className="privacy-consent-personal-data-item">
            {item}
          </p>
        ))}
      </div>
    </div>
  );
};

/** Syncs a custom scrollbar with horizontal table scroll. */
const useTableHorizontalScroll = () => {
  const tableContainerRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [visibleWidth, setVisibleWidth] = useState(0);
  const [totalWidth, setTotalWidth] = useState(0);

  const readScrollState = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    setScrollLeft(container.scrollLeft);
    setVisibleWidth(container.clientWidth);
    setTotalWidth(container.scrollWidth);
  }, []);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    readScrollState();

    const observer = new ResizeObserver(readScrollState);
    observer.observe(container);

    return () => observer.disconnect();
  }, [readScrollState]);

  const hiddenScrollWidth = Math.max(totalWidth - visibleWidth, 0);
  const thumbTrackWidth = Math.max(visibleWidth - SCROLLBAR_THUMB_WIDTH, 0);
  const thumbLeft =
    hiddenScrollWidth > 0 ? (scrollLeft / hiddenScrollWidth) * thumbTrackWidth : 0;
  const showScrollbar = totalWidth > visibleWidth + 1;

  const scrollTableToThumbPosition = useCallback(
    (nextThumbLeft) => {
      const container = tableContainerRef.current;
      if (!container || thumbTrackWidth <= 0) return;

      const clampedThumbLeft = Math.min(Math.max(nextThumbLeft, 0), thumbTrackWidth);
      container.scrollLeft = (clampedThumbLeft / thumbTrackWidth) * hiddenScrollWidth;
    },
    [hiddenScrollWidth, thumbTrackWidth],
  );

  return {
    tableContainerRef,
    readScrollState,
    showScrollbar,
    scrollLeft,
    hiddenScrollWidth,
    thumbLeft,
    scrollTableToThumbPosition,
  };
};

const TableHorizontalScrollbar = ({
  scrollLeft,
  maxScrollLeft,
  thumbLeft,
  onTrackClick,
  onThumbDrag,
}) => (
  <div
    className="privacy-consent-custom-scrollbar"
    style={{ marginTop: `${SCROLLBAR_GAP}px` }}
    role="scrollbar"
    aria-controls="privacy-consent-table"
    aria-valuenow={Math.round(scrollLeft)}
    aria-valuemin={0}
    aria-valuemax={Math.round(maxScrollLeft)}
    onPointerDown={onTrackClick}
  >
    <div
      className="privacy-consent-custom-scrollbar-thumb"
      style={{ transform: `translateX(${thumbLeft}px)` }}
      onPointerDown={onThumbDrag}
    />
  </div>
);

const PersonalDataTableBody = () => (
  <tbody>
    <tr>
      <th scope="row" className="privacy-consent-table-row-header">
        Personal Data
      </th>
      {PERSONAL_DATA_ROWS.map((column, index) => (
        <td key={`pd-${index}`} className="privacy-consent-table-cell">
          <PersonalDataCell data={column.personalData} />
        </td>
      ))}
    </tr>
    <tr>
      <th scope="row" className="privacy-consent-table-row-header">
        Purpose of Processing
      </th>
      {PERSONAL_DATA_ROWS.map((column, index) => (
        <td key={index} className="privacy-consent-table-cell privacy-consent-table-cell-purpose">
          {column.purpose}
        </td>
      ))}
    </tr>
    <tr>
      <th scope="row" className="privacy-consent-table-row-header">
        Legal Basis of Processing
      </th>
      {PERSONAL_DATA_ROWS.map((column, index) => (
        <td key={index} className="privacy-consent-table-cell privacy-consent-table-cell-legal">
          {column.legalBasis}
        </td>
      ))}
    </tr>
  </tbody>
);

const PersonalDataTable = () => {
  const {
    tableContainerRef,
    readScrollState,
    showScrollbar,
    scrollLeft,
    hiddenScrollWidth,
    thumbLeft,
    scrollTableToThumbPosition,
  } = useTableHorizontalScroll();

  const handleTrackClick = (event) => {
    if (event.target !== event.currentTarget) return;

    const trackLeft = event.currentTarget.getBoundingClientRect().left;
    scrollTableToThumbPosition(event.clientX - trackLeft - SCROLLBAR_THUMB_WIDTH / 2);
  };

  const handleThumbDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const container = tableContainerRef.current;
    if (!container) return;

    const dragStartX = event.clientX;
    const dragStartScrollLeft = container.scrollLeft;
    const thumbTrackWidth = Math.max(container.clientWidth - SCROLLBAR_THUMB_WIDTH, 0);
    const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);

    const onMove = (moveEvent) => {
      const dragDistance = moveEvent.clientX - dragStartX;
      container.scrollLeft = dragStartScrollLeft + (dragDistance / thumbTrackWidth) * maxScrollLeft;
    };

    const onEnd = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
  };

  return (
    <section className="privacy-consent-section" aria-labelledby="personal-data-collect-heading">
      <h2 id="personal-data-collect-heading" className="privacy-consent-section-title">
        Personal Data We Collect and Purpose of Processing:
      </h2>
      <p className="privacy-consent-body">
        We may collect and process the following types of personal data:
      </p>

      <div className="privacy-consent-table-scroll">
        <div ref={tableContainerRef} className="privacy-consent-table-wrapper" onScroll={readScrollState}>
          <table id="privacy-consent-table" className="privacy-consent-table">
            <PersonalDataTableBody />
          </table>
        </div>

        {showScrollbar && (
          <TableHorizontalScrollbar
            scrollLeft={scrollLeft}
            maxScrollLeft={hiddenScrollWidth}
            thumbLeft={thumbLeft}
            onTrackClick={handleTrackClick}
            onThumbDrag={handleThumbDrag}
          />
        )}
      </div>
    </section>
  );
};

const PRIVACY_EMAIL = 'globalprivacy@cipla.com';

const PrivacyEmailLink = () => (
 <strong><a href={`mailto:${PRIVACY_EMAIL}`} className="privacy-consent-email-link">
    {PRIVACY_EMAIL}
  </a>
</strong>
);

const PrivacyCommitmentSection = () => (
  <section className="privacy-consent-section">
    <p className="privacy-consent-body">
      Cipla is committed to protecting your privacy and your personal data. We encourage you to
      read this Privacy Notice.
    </p>
    <p className="privacy-consent-body privacy-consent-body-spaced">
      If you have any questions or comments about this Privacy Notice or how Cipla processes your
      personal data, please contact the Global Data Privacy Team at <PrivacyEmailLink />.
    </p>
    <p className="privacy-consent-body privacy-consent-body-spaced">
      You also have the right to lodge a complaint with the relevant data protection authority in
      your country.
    </p>
  </section>
);

const DATA_PRINCIPAL_RIGHTS = [
  {
    title: 'Right to Access',
    description: 'Obtain a summary of your personal data processed by us.',
  },
  {
    title: 'Right to Correction and Erasure',
    description:
      'Request correction, completion, updating and erasure of personal data including any inaccurate, incomplete or misleading Information provided for the processing, along with request for deletion once it is no longer required for the purpose for which it was collected or when consent is withdrawn.',
  },
  {
    title: 'Right to Nominate',
    description: 'Nominate another person to exercise your rights regarding your personal data.',
  },
  {
    title: 'Right to Grievance Redressal',
    description: 'Raise a complaint in case of any violation of your rights.',
  },
];

const DataPrincipalRightsSection = () => (
  <section className="privacy-consent-section" aria-labelledby="data-principal-rights-heading">
    <h2 id="data-principal-rights-heading" className="privacy-consent-section-title">
      Rights of Data Principals:
    </h2>
    <p className="privacy-consent-body">You have the following rights:</p>
    <ul className="privacy-consent-list privacy-consent-list-disc">
      {DATA_PRINCIPAL_RIGHTS.map((right) => (
        <li key={right.title}>
          <strong>{right.title}</strong> &ndash; {right.description}
        </li>
      ))}
    </ul>
    <p className="privacy-consent-body privacy-consent-body-spaced">
      To exercise your rights or raise any grievances, please contact us at <PrivacyEmailLink />
    </p>
  </section>
);

const GrievanceRedressalSection = () => (
  <section className="privacy-consent-section" aria-labelledby="grievance-redressal-heading">
    <h2 id="grievance-redressal-heading" className="privacy-consent-section-title">
      Grievance Redressal and Complaints:
    </h2>
    <p className="privacy-consent-body">
      If you believe your personal data has been processed in violation of this Notice, you may:
    </p>
    <ol className="privacy-consent-list privacy-consent-list-decimal">
      <li>
        Contact our <strong>privacy office</strong> at <PrivacyEmailLink />
      </li>
      <li>If unresolved, lodge a complaint with the <strong>Data Protection Board of India</strong>.</li>
    </ol>
    <p className="privacy-consent-body privacy-consent-body-spaced">
      Note: Please sign the acknowledgement form
    </p>
  </section>
);

const InfoIcon = () => (
  <span className="privacy-consent-info-icon" aria-hidden="true">
    i
  </span>
);

export const PrivacyConsent = ({ lastUpdated }) => {
  const updatedDate = lastUpdated ? new Date(lastUpdated) : new Date('2026-05-16');

  return (
    <div className="privacy-consent-page">
      <header className="privacy-consent-header">
          <h1 className="privacy-consent-title">Privacy Notice</h1>
          <p className="privacy-consent-updated">
            Last updated on : {formatLastUpdated(updatedDate)}
          </p>
      </header>

      <section role="status" className="privacy-consent-alert">
          <InfoIcon />
          <p className="privacy-consent-alert-text">
            Please scroll down to read the privacy notice &amp; proceed.
          </p>
      </section>

      <section className="privacy-consent-section" aria-labelledby="privacy-intro-heading">
          <h2 id="privacy-intro-heading" className="privacy-consent-section-title">
            Introduction
          </h2>
          <p className="privacy-consent-body privacy-consent-intro">
            Cipla Digital Health Ltd. (hereafter referred to as{' '}
            <strong>&ldquo;we&rdquo;, &ldquo;our&rdquo;</strong> or <strong>&ldquo;us&rdquo;</strong>) respects your
            privacy and is committed to protecting your Personal Data. In this Privacy Notice{' '}
            <strong>&ldquo;You&rdquo;</strong> or <strong>&ldquo;Your&rdquo;</strong> refers to{' '}
            <strong>Data Principal</strong> whose Personal Data is collected and processed by us.
            This Privacy Notice is issued under the{' '}
            <strong>
              Digital Personal Data Protection Act, 2023 (&ldquo;DPDP Act&rdquo;) and Digital
              Personal Data Protection Rules 2025.
            </strong>
          </p>
      </section>

      <PersonalDataTable />
      {/* <PrivacyCommitmentSection /> */}
      <DataPrincipalRightsSection />
      <GrievanceRedressalSection />
    </div>
  );
};

